import React from 'react';
import { useSelector } from 'react-redux';
import { Typography, Chip, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { isEmpty, get } from 'lodash';

import { Button, Dialog, SnackBar, Box } from 'components';
import { Logo } from 'assets';
import { getInvestorExistingEmails, referInvestor } from 'services';
import { emailRegex } from 'common/utils/constants';
import { errorMessageHandler } from 'common/utils/helpers';
import { RootState } from 'redux-modules/store/rootState';

import styles from './styles';

type Props = {
  type: string;
  onSuccessInviteCall?: () => void;
  dealId?: string;
  loading?: boolean;
};

const ReferralInviteBox = ({
  type,
  onSuccessInviteCall,
  dealId,
  loading,
}: Props) => {
  const classes = styles();

  const [emails, setEmails] = React.useState<any>([]);
  const [inputValue, setInputValue] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [isValidEmail, setIsValidEmail] = React.useState<boolean>(true);
  const [previewEmail, setPreviewEmail] = React.useState<boolean>(false);
  const [openSnackBar, setOpenSnackBar] = React.useState<boolean>(false);

  const { name } = useSelector(({ Auth }: RootState) => Auth.user);

  const handleCloseSnackBar = (
    event: React.SyntheticEvent | React.MouseEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackBar(false);
  };

  const handleKeyDown = (event: any) => {
    const inputValue = event.target.value.trim();
    const fieldValue = emails
      .concat(inputValue)
      .map((x: string) => x.trim())
      .filter((x: string) => x);
    switch (event.key) {
      case 'Tab': {
        event.preventDefault();
        event.stopPropagation();
        if (inputValue.length > 0) {
          handleEmailChange(inputValue, fieldValue);
        }
        break;
      }
      case ',': {
        event.preventDefault();
        event.stopPropagation();
        if (inputValue.length > 0) {
          handleEmailChange(inputValue, fieldValue);
        }
        break;
      }
      default:
    }
  };

  const handleEmailChange = (eventValue: string, value: any[]) => {
    const email = eventValue?.trim().replace(',', '');
    if (!isEmpty(email)) {
      if (!validateEmail(email)) {
        setIsValidEmail(false);
        setErrorMessage(`${email} is not a valid email address.`);
        return false;
      }

      if (emails.includes(email)) {
        setIsValidEmail(false);
        setErrorMessage(`${email} has already been added.`);
        return false;
      }
      setEmails([...emails, email]);
    } else {
      setErrorMessage('');
      setIsValidEmail(true);
      setEmails(value);
    }
    return true;
  };

  const validateEmail = (email: string) => {
    setErrorMessage('');
    setIsValidEmail(true);
    if (emailRegex.test(email)) {
      return true;
    } else {
      return false;
    }
  };

  const handleSubmit = async () => {
    if (isEmpty(emails) && !isEmpty(inputValue)) {
      const fieldValue = emails
        .concat(inputValue)
        .map((x: string) => x.trim())
        .filter((x: string) => x);
      if (inputValue.length > 0) {
        setIsLoading(true);
        const res = await handleEmailChange(inputValue, fieldValue);
        setIsLoading(false);
        if (res) sendReferAndEarnInvite(fieldValue);
      }
    } else {
      sendReferAndEarnInvite(emails);
    }
  };

  const sendReferAndEarnInvite = (emails: string[]) => {
    if (!isEmpty(emails)) {
      setIsLoading(true);
      setErrorMessage('');
      getInvestorExistingEmails({
        portal: 'INVESTOR',
        emails: emails,
      })
        .then((res: any) => {
          if (isEmpty(get(res, 'data'))) {
            referInvestors(emails);
          } else {
            setErrorMessage(`${get(res, 'data')} is already registered.`);
            setIsLoading(false);
          }
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          setErrorMessage(message);
          setIsLoading(false);
        });
    } else {
      setErrorMessage('Please add one email to send invite.');
      setIsLoading(false);
    }
  };

  const referInvestors = (emails: string[]) => {
    const inviteObject = {
      referType: type,
      emails: emails,
      ...(type === 'DEAL' && { dealId: dealId }),
    };
    referInvestor(inviteObject)
      .then(() => {
        setEmails([]);
        if (onSuccessInviteCall) {
          onSuccessInviteCall();
        }
        setOpenSnackBar(true);
        setIsLoading(false);
      })
      .catch((err) => {
        const message: string = errorMessageHandler(err);
        setErrorMessage(message);
        setIsLoading(false);
      });
  };

  return (
    <>
      <Box className={classes.inviteEmailContainer}>
        <Box className={classes.inviteInput}>
          <Autocomplete
            limitTags={10}
            multiple
            freeSolo
            id="tags-filled"
            options={[]}
            value={emails}
            inputValue={inputValue}
            onChange={(event: any, value: any) => {
              const fieldValue = value
                .map((x: string) => x.trim())
                .filter((x: string) => x);
              handleEmailChange(event.target.value, fieldValue);
            }}
            onInputChange={(_: any, newInputValue: string) => {
              setInputValue(newInputValue);
            }}
            className={classes.inviteEmail}
            renderTags={(value: string[], getTagProps) => {
              return value
                .filter((item: any) => emailRegex.test(item))
                .map((option: string, index: number) => (
                  <Chip
                    key={index}
                    className={classes.chip}
                    size="small"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ));
            }}
            renderInput={(params: any) => {
              params.inputProps.onKeyDown = handleKeyDown;
              return (
                <TextField
                  {...params}
                  className={classes.autoCompleteInput}
                  variant="outlined"
                  label=""
                  error={!isValidEmail}
                  onChange={() => {
                    setErrorMessage('');
                    setIsValidEmail(true);
                  }}
                  placeholder={
                    !isEmpty(emails)
                      ? ''
                      : 'Type email addresses with commas or Press `Enter`'
                  }
                />
              );
            }}
          />
        </Box>
        <Box className={classes.sendInviteBox}>
          <Button
            name="Send Invite"
            onClick={handleSubmit}
            className={classes.sendInviteButton}
            isLoading={isLoading}
            disabled={isLoading || loading}
          />
        </Box>
        <Box className={classes.previewEmailBox}>
          <Typography
            className={classes.previewEmailText}
            onClick={() => setPreviewEmail(true)}
          >
            Preview Email
          </Typography>
        </Box>
        {!isEmpty(errorMessage) && (
          <Typography variant="body1" className={classes.errorMessage}>
            {errorMessage}
          </Typography>
        )}
      </Box>
      <SnackBar
        open={openSnackBar}
        handleClose={handleCloseSnackBar}
        title="The invite has been sent successfully"
        type="success"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      />
      <Dialog
        open={previewEmail}
        maxWidth={'sm'}
        title=""
        buttonText="Continue"
        handleClose={() => setPreviewEmail(false)}
      >
        <Typography className={classes.emailInvitationSubjectTxt}>
          Subject: {name} has invited you to invest in best-in-class Startups on
          8vdX
        </Typography>
        <Typography className={classes.emailInvitationText}>
          You&#39;ve Been Invited
          <br />
          Congratulations, {name} has invited you to 8vdX platform.
        </Typography>
        <Typography className={classes.emailInvitationText}>
          8vdX provides investors early access to a high potential and
          diversified pool of startups. Investors get an opportunity to fund
          startups backed by the world’s largest Startup Accelerator, Y
          Combinator. We are extremely selective in the startups that are
          included in any of the deals present on our platform. Y Combinator
          accepts less than 2% of all the startups that apply to them, on top of
          this we run our own diligence and hand pick high growth startups.
          Minimums are as low as $10k making it easy to diversify your
          portfolio, spread risk, and invest in startups.
        </Typography>
        <Typography className={classes.emailInvitationText}>
          By signing up with this invitation, you can begin your investment
          journey with 8vdX.
        </Typography>
        <Button name="Accept Invite" className={classes.acceptInviteButton} />
        <Typography className={classes.emailInvitationText} gutterBottom>
          We look forward to welcoming you to 8vdX!
        </Typography>
        <Typography className={classes.emailInvitationText}>
          8vdX Team
        </Typography>
        <img src={Logo} alt={Logo} className={classes.logoImg} />
      </Dialog>
    </>
  );
};

export default ReferralInviteBox;
